import type { ReactNode } from 'react';
import type { Assertion, Outcome, RunStep } from '@sfpbxtestbot/contracts';
import { EvidenceItem } from './Evidence.js';
import { EmptyState, StatusBadge, formatDuration } from './primitives.js';

const MARKER: Record<Outcome, string> = {
  passed: '✓',
  warning: '!',
  failed: '✕',
  blocked: '✕',
  running: '•',
  pending: '',
  skipped: '–',
};

/**
 * The run timeline.
 *
 * Ordering is the point: someone catching up mid-run should be able to read
 * top-to-bottom and know where the bot got to. Failures lead with the reason in
 * plain words; the stack trace stays folded away because it is only useful to
 * whoever is going to fix it.
 */
export function RunTimeline({
  runId,
  steps,
}: {
  runId: string;
  steps: RunStep[];
}): ReactNode {
  if (steps.length === 0) {
    return (
      <EmptyState
        title="Waiting for the first step"
        body="The runner is starting up. Steps appear here the moment it begins working through them."
      />
    );
  }

  return (
    <div className="timeline">
      {steps.map((step, index) => (
        <article className="timeline__item" key={step.stepKey}>
          <div className="timeline__rail">
            <span className={`timeline__marker timeline__marker--${step.outcome}`} aria-hidden="true">
              {MARKER[step.outcome]}
            </span>
            {index < steps.length - 1 ? <span className="timeline__line" /> : null}
          </div>

          <div className="timeline__body">
            <div className="timeline__head">
              <span className="timeline__title">{step.title}</span>
              <span className="timeline__meta">
                <StatusBadge outcome={step.outcome} />
                {step.durationMs != null ? ` · ${formatDuration(step.durationMs)}` : ''}
              </span>
            </div>

            {step.summary ? <p className="timeline__summary">{step.summary}</p> : null}

            {step.reason ? <p className="timeline__reason">{step.reason}</p> : null}

            {step.assertions.length > 0 ? (
              <ul className="assertions">
                {step.assertions.map((assertion) => (
                  <AssertionRow key={assertion.id} assertion={assertion} />
                ))}
              </ul>
            ) : null}

            {step.evidence.length > 0 ? (
              <div className="row" style={{ gap: 12, fontSize: 13 }}>
                {step.evidence.map((item) => (
                  <EvidenceItem key={item.id} runId={runId} item={item} />
                ))}
              </div>
            ) : null}

            {step.errorDetail ? (
              <details className="disclosure">
                <summary>Technical detail</summary>
                <pre className="code-block">{step.errorDetail}</pre>
              </details>
            ) : null}
          </div>
        </article>
      ))}
    </div>
  );
}

function AssertionRow({ assertion }: { assertion: Assertion }): ReactNode {
  const showComparison =
    assertion.outcome !== 'passed' && (assertion.expected != null || assertion.actual != null);

  return (
    <li className="assertion">
      <StatusBadge outcome={assertion.outcome} label={assertion.outcome === 'passed' ? 'OK' : undefined} />
      <div className="assertion__detail">
        <span>{assertion.title}</span>
        {showComparison ? (
          <span className="assertion__compare">
            expected {assertion.expected ?? '—'} · found {assertion.actual ?? '—'}
          </span>
        ) : null}
        {assertion.outcome !== 'passed' && assertion.impact ? (
          <span className="assertion__impact">{assertion.impact}</span>
        ) : null}
      </div>
    </li>
  );
}
